import {
  Box,
  Button,
  FieldError,
  Flexbox,
  Label,
  Stack,
  StackRow,
  shootToast,
  BoxFileMultiple,
} from "codekit";
import { useForm } from "react-hook-form";
import { useRef, useState, useEffect } from "react";
import {
  useCreatePackagePresignedMutation,
  useConfirmPackagePresignedMutation,
  useCreatePackageMutation,
} from "../../libs/redux/slices/Genioxxx/genioxxxApiSlice";

const Carguedo = ({
  setShowGenioModal,
  genioParams,
  jsonMetx,
  onUploadSuccess,
  onFilesChange,
  onUploadingChange,
  sendOnDrop = true,
  documentDescription = ""
}) => {
  const uploadRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const sendingRef = useRef(false);

  const [createPackagePres] = useCreatePackagePresignedMutation();
  const [createPackage] = useCreatePackageMutation();
  const [confirmPackagePresigned] = useConfirmPackagePresignedMutation();

  const methods = useForm({
    mode: "onBlur",
    defaultValues: { files: [] },
  });

  const {
    setValue,
    register,
    formState: { errors },
  } = methods;

  const tresMegas = 3 * 1024 * 1024;

  const fileToBase64Payload = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = String(reader.result || "");
        const base64 = dataUrl.split(",")[1] || "";
        const mime = file.type || "";
        resolve(`${mime},${base64}`);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const buildPayload = (file, filebs64) => ({
    ...genioParams,
    JSONMETX: jsonMetx,
    FILEBS64: filebs64,
    TYPEXXXX: file.type || "",
    NAMEXXXX: file.name || "",
  });

  const uploadFileToGenio = async (file) => {
    let IDARCHXX = null;
    let docUrl = null;
    let path = null;

    try {
      if (file.type === "application/pdf" && file.size > tresMegas) {
        const dataPackagePres = {
          ...genioParams,
          JSONMETX: jsonMetx,
          TYPEXXXX: file.type,
          NAMEXXXX: file.name,
        };

        const response = await createPackagePres(dataPackagePres).unwrap();
        const { url: presignedUrl, IDARCHXX: idArchxxPres } = response.data;

        IDARCHXX = idArchxxPres;
        docUrl = presignedUrl;

        await fetch(docUrl, { method: "PUT", body: file, redirect: "follow" });

        await confirmPackagePresigned({
          path: { IDARCHXX },
          body: { REGESTXX: "ACTIVO" },
        }).unwrap();

        return {
          IDARCHXX,
          status: "success",
          fileName: file.name,
          docUrl,
          originalFile: file,
        };
      }

      const filebs64 = await fileToBase64Payload(file);
      const payload = buildPayload(file, filebs64);

      const response = await createPackage(payload).unwrap();

      IDARCHXX = response.IDARCHXX;
      path = response.PATHFILX;
      docUrl = response.DOCDURLX;

      return {
        IDARCHXX,
        status: "success",
        fileName: file.name,
        docUrl,
        path,
        originalFile: file,
      };
    } catch (err) {
      console.error(`Error al cargar el archivo ${file?.name}:`, err);
      return {
        status: "error",
        fileName: file?.name || "archivo",
        error: err,
        originalFile: file,
      };
    }
  };

  const handleSendPackages = async (filesToSend) => {
    if (!filesToSend?.length) return;

    if (sendingRef.current) return;
    sendingRef.current = true;

    setIsSending(true);

    try {
      const queue = [...filesToSend];

      for (const file of queue) {
        const result = await uploadFileToGenio(file);

        setFiles((prev) => {
          const updated = prev.filter((f) => f !== file);
          setValue("files", updated);
          onFilesChange?.(updated);
          return updated;
        });

        if (result.status === "success") {
          shootToast({
            text: `${file.name} cargado exitosamente.`,
            variant: "success",
            autoHide: true,
            duration: 2000,
          });
        } else {
          shootToast({
            text: `Error al cargar ${file.name}.`,
            variant: "error",
            autoHide: true,
            duration: 4000,
          });
        }

        onUploadSuccess?.([result]);
      }

      setShowGenioModal?.(false);
    } finally {
      setIsSending(false);
      sendingRef.current = false;
    }
  };

  const isAllowed = (f) => {
    const type = f?.type || "";
    const name = f?.name || "";

    const allowedExtensions = [".xls", ".xlsx", ".doc", ".pdf", ".jpg", ".png", ".xml"];
    const fileExtension = name.substring(name.lastIndexOf(".")).toLowerCase();

    return (
      type.startsWith("image/") ||
      type === "application/pdf" ||
      allowedExtensions.includes(fileExtension)
    );
  };

  const handleChangeFiles = (incoming) => {
    const newFiles = Array.isArray(incoming) ? incoming : [incoming];
    const onlyAllowed = newFiles.filter(isAllowed);

    if (onlyAllowed.length > 0) {
      setFiles((prev) => {
        const existingKeys = new Set(prev.map((f) => `${f.name}-${f.size}`));
        const uniqueAllowed = onlyAllowed.filter(
          (f) => !existingKeys.has(`${f.name}-${f.size}`)
        );

        const updatedFiles = [...prev, ...uniqueAllowed];
        setValue("files", updatedFiles, { shouldValidate: true });
        onFilesChange?.(updatedFiles);
        return updatedFiles;
      });
    }

    const rejected = newFiles.filter((f) => !isAllowed(f));
    if (rejected.length) {
      shootToast({
        variant: "warning",
        text: "Solo se permiten imágenes o archivos PDF. Se ignoraron algunos archivos.",
        duration: 3000,
        autoHide: true,
      });
    }
  };

  const handleRemoveFileMultiple = (index) => {
    if (typeof index === "number") {
      setFiles((prev) => {
        const nextFiles = prev.filter((_, i) => i !== index);
        setValue("files", nextFiles);
        onFilesChange?.(nextFiles);
        return nextFiles;
      });
    } else {
      setFiles([]);
      setValue("files", []);
      onFilesChange?.([]);
    }
  };

  const handleUploadFile = () => {
    uploadRef.current?.click();
  };

  const handleDrag = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    const droppedFiles = Array.from(event.dataTransfer?.files || []);
    handleChangeFiles(droppedFiles);
  };

  useEffect(() => {
    if (!sendOnDrop) return;
    if (!files.length) return;
    if (sendingRef.current) return;

    handleSendPackages([...files]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendOnDrop, files.length]);

  useEffect(() => {
    onUploadingChange?.(isSending);
  }, [isSending, onUploadingChange]);

  return (
    <Box mt={4}>
      <Label>Adjuntar Soporte { documentDescription }</Label>
      <Box
        border={1}
        borderColor="neutral-300"
        spacing={3}
        rounded="md"
        bg={`${dragActive ? "neutral-300" : "neutral-200"}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <Flexbox justifyContent="center" alignItems="center" minHeight={140}>
          <Stack gap={3} justifyItems="center">
            <StackRow>
              <BoxFileMultiple
                bg="neutral-200"
                spacing={4}
                rounded="md"
                borderColor="neutral-400"
                maxFiles={100}
                files={files}
                onChangeFile={handleChangeFiles}
                onRemoveFile={handleRemoveFileMultiple}
                extensions={[".xls", ".xlsx", ".doc", ".pdf", ".jpg", ".png", ".xml"]}
              >
                <Box mt={4}>
                  <Stack justifyItems="center" gap={4}>
                    <Button
                      type="button"
                      variant="secondary-bold"
                      icon="files-plus"
                      onClick={handleUploadFile}
                      isLoading={isSending}
                      disabled={isSending}
                    >
                      {isSending ? "Cargando..." : "Seleccionar archivos"}
                    </Button>
                    <input
                      ref={uploadRef}
                      type="file"
                      multiple
                      accept=".xls,.xlsx,.doc,.pdf,.jpg,.png,.xml,image/*,application/pdf"
                      style={{ display: "none" }}
                      onChange={(e) =>
                        handleChangeFiles(Array.from(e.target.files || []))
                      }
                      disabled={isSending}
                    />
                    <input type="hidden" {...register("files", { required: true })} />
                  </Stack>
                </Box>
              </BoxFileMultiple>
            </StackRow>
          </Stack>
        </Flexbox>
      </Box>
      {errors.files && <FieldError>{errors.files.message}</FieldError>}
    </Box>
  );
};

export default Carguedo;
